import { initFeatureFlags } from '@janeapp/feature-flags-client';
import { FeatureFlags as FeatureFlagsClient } from '@janeapp/feature-flags-client/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FeatureFlagsApi } from '~/api';
import { LoadingView } from '~/components';
import { getBaseUrl } from '~/env';
import { ErrorLevel, logErrorToSentry } from '~/utilities';

interface FeatureFlagsProviderProps {
  children?: React.ReactNode | JSX.Element | JSX.Element[];
}

export const FeatureFlagsProvider = ({
  children,
}: FeatureFlagsProviderProps) => {
  const { isLoading, isSuccess, isError } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: async () => {
      const api = new FeatureFlagsApi({ baseUrl: getBaseUrl() });
      const response = await api.getClientConfig();
      const data = await response.json();

      await initFeatureFlags(() => Promise.resolve(data));
      return data;
    },
    retry: 1, // Retry once = 2 total attempts
    retryDelay: 1000, // 1 second delay between retries
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    throwOnError(error, _query) {
      logErrorToSentry({
        type: ErrorLevel.Error,
        message: `Failed to initialize feature flags after 2 attempts: ${error}`,
      });
      return false;
    },
  });

  return (
    <FeatureFlagsClient.Provider>
      <>
        {isLoading && (
          <div className="flex h-screen w-full items-center justify-center">
            <LoadingView size="4xl" loading={true} message={''} />
          </div>
        )}
        {!isLoading && (isSuccess || isError) && children}
      </>
    </FeatureFlagsClient.Provider>
  );
};
